import React from 'react';
import styled from 'styled-components';
import {COLORS} from '../styleVars';
import {Typography} from '../typography/Typography';
import {Image} from './Image';
import {ImageSrc} from './ImageUtils';

type BadgePosition = 1 | 2 | 3 | 4 | 5 | 6;

interface ContainerProps {
  position: BadgePosition;
}

const bottomLeftPositionStyle = 'bottom: 0; left: -5px';
const defaultPositionStyle = 'bottom: 0; right: -5px;';
const badgePositionStyle = {
  1: defaultPositionStyle,
  2: 'top: 0; right: -5px;',
  3: 'top: 0; left: -5px;',
  4: 'top: 15px; left: -5px;',
  5: 'top: 30px; left: -5px;',
  6: bottomLeftPositionStyle,
  default: defaultPositionStyle,
};

const Container = styled(Typography)<ContainerProps>`
  background-color: ${COLORS.white};
  box-shadow: 0 0 2px 0 ${COLORS.grey};
  border-radius: 50%;
  ${(props) => badgePositionStyle[props.position] || badgePositionStyle['default']};
  position: absolute;
  display: inline-flex;
  width: 20px;
  height: 20px;
  z-index: 2;
  justify-content: center;
  align-items: center;
  align-content: center;

  img {
    display: block;
    margin: auto;
  }
`;

interface BadgeProps {
  /**
   * position of the badge inside of the image-container
   */
  position: BadgePosition;
  /**
   * @ignore
   */
  className?: string;
}

interface ImageBadgeProps extends BadgeProps {
  /**
   * define if image should fit inside badge without being cut off
   */
  fit?: boolean;
  /**
   * value of the badge
   */
  value: ImageSrc;
}

interface IconBadgeProps extends BadgeProps {
  /**
   * Set color of the icon
   */
  color?: string;
  /**
   * value of the badge
   */
  value: SVGElement;
}

interface TextBadgeProps extends BadgeProps {
  /**
   * value of the badge
   */
  value: string;
}

const ImageBadge: React.FC<ImageBadgeProps> = ({value, position, className, fit}) => {
  return (
    <Container position={position} className={className} variant='caption3'>
      <Image entity='emblem' src={value} width={16} height={16} fit={fit} />
    </Container>
  );
};

const IconBadge: React.FC<IconBadgeProps> = ({value, position, className}) => {
  return (
    <Container position={position} className={className} variant='caption3'>
      {value}
    </Container>
  );
};

const TextBadge: React.FC<TextBadgeProps> = ({value, position, className}) => {
  return (
    <Container position={position} className={className} variant='caption3'>
      <span>{value}</span>
    </Container>
  );
};

export {ImageBadge, IconBadge, TextBadge};
